import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import { sortFocusSkillsByOrder } from '@/lib/utils';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

export type CourseForPullData = {
	course_id: string;
	course_name: string;
	courses_outcomes: Array<{
		courses_outcomes_id: string;
		outcome: {
			outcome_id: string;
			outcome_order: number;
			outcome_text: string | null;
		} | null;
	}>;
	courses_focuses: Array<{
		courses_focuses_id: string;
		focus: {
			focus_id: string;
			focus_order: number;
			focus_title: string | null;
			focus_type: string | null;
			focuses_skills?: Array<{
				focuses_skills_id: string;
				skill_order: number;
				skill: { skill_id: string; skill_text: string | null; skill_type: string | null };
			}>;
		} | null;
	}>;
};

export function useGetCourseForPull(courseId: string | null) {
	return useQuery({
		queryKey: ['get-course-for-pull', courseId, schoolId],
		queryFn: async () => {
			if (!courseId) return null;

			const { data, error } = await supabase
				.from('courses')
				.select(
					`
					course_id,
					course_name,
					courses_outcomes (
						courses_outcomes_id,
						outcome:outcomes (
							outcome_order,
							outcome_text,
							outcome_id
						)
					),
					courses_focuses (
						courses_focuses_id,
						focus:focuses (
							focus_title,
							focus_type,
							focus_order,
							focus_id,
							focuses_skills (
								focuses_skills_id,
								skill_order,
								skill:skills (
									skill_id,
									skill_text,
									skill_type
								)
							)
						)
					)
				`
				)
				.eq('course_id', courseId)
				.eq('school_id', schoolId)
				.maybeSingle();

			if (error) throw error;
			if (!data) return null;

			const row = data as unknown as CourseForPullData;

			row.courses_outcomes?.sort(
				(a, b) => (a.outcome?.outcome_order ?? 0) - (b.outcome?.outcome_order ?? 0)
			);
			row.courses_focuses?.sort(
				(a, b) => (a.focus?.focus_order ?? 0) - (b.focus?.focus_order ?? 0)
			);
			for (const cf of row.courses_focuses ?? []) {
				if (cf.focus && cf.focus.focuses_skills === undefined) {
					cf.focus.focuses_skills = [];
				}
			}
			sortFocusSkillsByOrder(
				row.courses_focuses as unknown as Parameters<typeof sortFocusSkillsByOrder>[0]
			);

			return row;
		},
		enabled: Boolean(courseId) && Boolean(schoolId),
		staleTime: 60_000,
	});
}
